import Vue from "vue";
import Vuex from "vuex";
import firebase from "firebase";
import router from "@/router";

Vue.use(Vuex);

export const store = new Vuex.Store({
  state: {
    appTitle: "Sensor de presencia automático",
    user: null,
    error: null,
    loading: false
  },
  mutations: {
    setUser(state, payload) {
      state.user = payload;
    },
    setError(state, payload) {
      state.error = payload;
    },
    setLoading(state, payload) {
      state.loading = payload;
    }
  },
  actions: {
    passwordRestore({ commit }, payload) {
      commit("setLoading", true);
      firebase
        .auth()
        .sendPasswordResetEmail(payload.email)
        .then(firebaseUser => {
          router.push("/confirmations/reset");
        })
        .catch(error => {
          commit("setError", error.message);
          commit("setLoading", false);
        });
      commit("setLoading", false);
    },
    userSignUp({ commit }, payload) {
      commit("setLoading", true);
      firebase
        .auth()
        .createUserWithEmailAndPassword(payload.email, payload.password)
        .then(firebaseUser => {
          commit("setUser", null);
          commit("setLoading", false);
          commit("setError", null);
          firebase
            .database()
            .ref("users/" + firebaseUser.user.uid)
            .update({
              uid: firebaseUser.user.uid,
              name: payload.name,
              username: payload.username,
              email: payload.email,
              language: payload.language,
              dateSignUp: new Date().toJSON(), // firebaseUser.user.metadata.creationTime,
              dateLastLogIn: new Date().toJSON(), // firebaseUser.user.metadata.lastSignInTime,
              description: ""
            });
          firebaseUser.user.sendEmailVerification();
          firebase.auth().signOut();
          router.push("/confirmations/created");
        })
        .catch(error => {
          commit("setError", error.message);
          commit("setLoading", false);
        });
      firebase.auth().signOut();
    },
    userSignIn({ commit }, payload) {
      commit("setLoading", true);
      firebase
        .auth()
        .setPersistence(
          payload.rememberMe
            ? firebase.auth.Auth.Persistence.LOCAL
            : firebase.auth.Auth.Persistence.SESSION
        );
      firebase
        .auth()
        .signInWithEmailAndPassword(payload.email, payload.password)
        .then(firebaseUser => {
          if (firebaseUser.user.emailVerified) {
            commit("setUser", {
              email: firebaseUser.user.email,
              emailVerified: firebaseUser.user.emailVerified,
              uid: firebaseUser.user.uid
            });
            firebase
              .database()
              .ref("users/" + firebaseUser.user.uid)
              .update({
                dateLastLogIn: new Date().toJSON()
              });
            commit("setLoading", false);
            commit("setError", null);
            router.push("/profile");
          } else {
            firebaseUser.user.sendEmailVerification();
            firebase.auth().signOut();
            commit("setError", "Email no verificado");
            commit("setLoading", false);
          }
        })
        .catch(error => {
          commit("setError", error.message);
          commit("setLoading", false);
        });
    },
    autoSignIn({ commit }, payload) {
      if (payload.emailVerified) {
        commit("setUser", {
          email: payload.email,
          emailVerified: payload.emailVerified,
          uid: payload.uid
        });
      } else {
        firebase.auth().signOut();
        commit("setUser", null);
      }
    },
    userSignOut({ commit }) {
      firebase.auth().signOut();
      commit("setUser", null);
      router.push("/");
    },
    checkUsername({ commit }, payload) {
      return new Promise((resolve, reject) => {
        firebase
          .database()
          .ref("users")
          .orderByChild("username")
          .equalTo(payload.username)
          .once("value")
          .then(function(snapshot) {
            if (snapshot.val() !== null) {
              reject();
            } else {
              resolve();
            }
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    changeProfileInfo({ commit }, payload) {
      firebase
        .database()
        .ref("users/" + payload.uid)
        .update({
          description: payload.description,
          name: payload.name
        });
    },
    changeObjectInfo({ commit }, payload) {
      firebase
        .database()
        .ref(payload.table + "/" + payload.key)
        .update({
          description: payload.description,
          name: payload.name
        });
    },
    syncTelegram({ commit }, payload) {
      firebase
        .database()
        .ref("users/" + payload.uid)
        .update({
          telegramID: payload.id
        });
    },
    unsyncTelegram({ commit }, payload) {
      firebase
        .database()
        .ref("users/" + payload.uid)
        .update({
          telegramID: null
        });
    },
    createGroup({ commit }, payload) {
      return new Promise((resolve, reject) => {
        const postKey = firebase
          .database()
          .ref("groups")
          .push({
            name: payload.name,
            description: payload.description,
            dateRegistration: new Date().toJSON(),
            owner: payload.owner
          }).key;
        firebase
          .database()
          .ref("groups/" + postKey)
          .update({
            key: postKey
          })
          .then(() => {
            resolve(postKey);
          });
      });
    },
    createBuilding({ commit }, payload) {
      return new Promise((resolve, reject) => {
        const postKey = firebase
          .database()
          .ref("buildings")
          .push({
            name: payload.name,
            description: payload.description,
            dateRegistration: new Date().toJSON(),
            owner: payload.owner
          }).key;
        firebase
          .database()
          .ref("buildings/" + postKey)
          .update({
            key: postKey
          })
          .then(() => {
            resolve(postKey);
          });
      });
    },
    createSensor({ commit }, payload) {
      const postKey = firebase
        .database()
        .ref("sensors")
        .push({
          name: payload.name,
          description: payload.description,
          dateRegistration: new Date().toJSON(),
          token: payload.code,
          owner: payload.owner
        }).key;
      firebase
        .database()
        .ref("sensors/" + postKey)
        .update({
          key: postKey
        });
    },
    createCodeSensor({ commit }, payload) {
      firebase
        .database()
        .ref("sensorCodes/" + payload.code)
        .update({
          name: payload.name,
          description: payload.description,
          dateRegistration: new Date().toJSON(),
          code: payload.code,
          owner: payload.owner,
          activeOwner: payload.owner,
          used: false
        });
    },
    endNewSensor({ commit }, payload) {
      firebase
        .database()
        .ref("sensorCodes/" + payload.code)
        .update({
          activeOwner: null,
          used: true
        });
    },
    endNewSensorPush({ commit }, payload) {
      firebase
        .database()
        .ref("sensorCodes/" + payload.code)
        .update({
          activeOwner: null,
          used: true
        });
      router.push("/dashboard/sensors");
    },
    changeReadNotification({ commit }, payload) {
      firebase
        .database()
        .ref("notifications/" + payload.key)
        .update({
          readed: payload.readed
        });
    },
    markItemAccepted({ commit }, payload) {
      firebase
        .database()
        .ref("notifications/" + payload.key)
        .update({
          accepted: true
        });
    },
    deleteObject({ commit }, payload) {
      firebase
        .database()
        .ref(payload.route + "/" + payload.key)
        .remove();
      if (payload.route === "sensors") {
        firebase
          .database()
          .ref("activations")
          .orderByChild("sensor_id")
          .equalTo(payload.key).on('child_added', function(snapshot) {
            snapshot.ref.remove();
          });
      }
      if (payload.route === "buildings") {
        //Borrar sensores
        firebase
          .database()
          .ref("sensors")
          .orderByChild("building")
          .equalTo(payload.key)
          .once("value")
          .then(function(snapshot) {
            if (snapshot.val() !== null) {
              let valores = Object.values(snapshot.val());
              for (let i = 0; i < valores.length; i++) {
                firebase
                  .database()
                  .ref("sensors/" + valores[i].key)
                  .update({
                    building: null
                  });
              }
            }
          });
      }
      if (payload.route === "buildings" || payload.route === "groups") {
        firebase
          .database()
          .ref(payload.route + "Sensors/" + payload.key)
          .remove();
      }
    },
    deleteCodeSensor({ commit }, payload) {
      firebase
        .database()
        .ref("sensorCodes/" + payload.code)
        .remove();
    },
    changePassword({ commit }, payload) {
      return new Promise((resolve, reject) => {
        commit("setLoading", true);
        let user = firebase.auth().currentUser;
        let cred = firebase.auth.EmailAuthProvider.credential(
          user.email,
          payload.currentPassword
        );
        user
          .reauthenticateWithCredential(cred)
          .then(() => {
            firebase
              .auth()
              .currentUser.updatePassword(payload.newPassword)
              .then(() => {
                commit("setLoading", false);
                resolve();
              })
              .catch(error => {
                commit("setError", error.toString());
                commit("setLoading", false);
                reject();
              });
          })
          .catch(error => {
            commit("setError", error.toString());
            commit("setLoading", false);
            reject();
          });
      });
    },
    changeEmail({ commit }, payload) {
      return new Promise((resolve, reject) => {
        commit("setLoading", true);
        let user = firebase.auth().currentUser;
        let cred = firebase.auth.EmailAuthProvider.credential(
          user.email,
          payload.password
        );
        user
          .reauthenticateWithCredential(cred)
          .then(() => {
            firebase
              .auth()
              .currentUser.updateEmail(payload.email)
              .then(() => {
                firebase
                  .database()
                  .ref("users/" + payload.uid)
                  .update({
                    email: payload.email
                  });
                user.sendEmailVerification();
                commit("setLoading", false);
                firebase.auth().signOut();
                commit("setUser", null);
                resolve();
              })
              .catch(error => {
                commit("setError", error.toString());
                commit("setLoading", false);
                reject();
              });
          })
          .catch(error => {
            commit("setError", error.toString());
            commit("setLoading", false);
            reject();
          });
      });
    },
    deleteAccount({ commit }, payload) {
      return new Promise((resolve, reject) => {
        commit("setLoading", true);
        let user = firebase.auth().currentUser;
        let cred = firebase.auth.EmailAuthProvider.credential(
          user.email,
          payload.password
        );
        user
          .reauthenticateWithCredential(cred)
          .then(() => {
            firebase
              .database()
              .ref("users/" + user.uid)
              .remove();
            firebase
              .database()
              .ref("sensorCodes")
              .orderByChild("owner")
              .equalTo(user.uid).on('child_added', function(snapshot) {
                snapshot.ref.remove();
              });
            firebase
              .database()
              .ref("notifications")
              .orderByChild("owner")
              .equalTo(user.uid).on('child_added', function(snapshot) {
                snapshot.ref.remove();
              });
            firebase
              .database()
              .ref("sensors")
              .orderByChild("owner")
              .equalTo(user.uid).on('child_added', function(snapshot) {
                if (snapshot.val() !== null) {
                  let valores = Object.values(snapshot.val());
                  for (let i = 0; i < valores.length; i++) {
                    firebase
                      .database()
                      .ref("activations")
                      .orderByChild("sensor_id")
                      .equalTo(valores[i].key).on('child_added', function(snapshot) {
                        snapshot.ref.remove();
                      });
                  }
                }
                snapshot.ref.remove();
              });
            firebase
              .database()
              .ref("groups")
              .orderByChild("owner")
              .equalTo(user.uid).on('child_added', function(snapshot) {
                if (snapshot.val() !== null) {
                  let valores = Object.values(snapshot.val());
                  for (let i = 0; i < valores.length; i++) {
                    firebase
                      .database()
                      .ref("groupsSensors/" + valores[i].key)
                      .remove();
                  }
                }
                snapshot.ref.remove();
              });
            firebase
              .database()
              .ref("buildings")
              .orderByChild("owner")
              .equalTo(user.uid).on('child_added', function(snapshot) {
                if (snapshot.val() !== null) {
                  let valores = Object.values(snapshot.val());
                  for (let i = 0; i < valores.length; i++) {
                    firebase
                      .database()
                      .ref("buildingsSensors/" + valores[i].key)
                      .remove();
                  }
                }
                snapshot.ref.remove();
              });
            user.delete();
            firebase.auth().signOut();
            commit("setUser", null);
            commit("setLoading", false);
            resolve();
          })
          .catch(error => {
            commit("setError", error.toString());
            commit("setLoading", false);
            reject();
          });
      });
    },
    changeLocation({ commit }, payload) {
      return new Promise((resolve, reject) => {
        firebase
          .database()
          .ref("buildings/" + payload.key)
          .update({
            location: payload.location
          })
          .then(() => {
            resolve();
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    removeLocation({ commit }, payload) {
      return new Promise((resolve, reject) => {
        firebase
          .database()
          .ref("buildings/" + payload.key)
          .update({
            location: null
          })
          .then(() => {
            resolve();
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    addSensores({ commit }, payload) {
      return new Promise((resolve, reject) => {
        if (payload.table === "buildingsSensors") {
          for (let i = 0; i < payload.anteriores.length; i++) {
            firebase
              .database()
              .ref("sensors/" + payload.anteriores[i].key)
              .update({
                building: null
              });
          }
          firebase
            .database()
            .ref("buildings" + "/" + payload.key)
            .update({
              num: payload.sensores.length
            });
          firebase
            .database()
            .ref(payload.table + "/" + payload.key)
            .update({
              key: payload.key,
              sensors: payload.sensores.sort()
            })
            .then(() => {
              if (payload.table === "buildingsSensors") {
                for (let i = 0; i < payload.sensores.length; i++) {
                  firebase
                    .database()
                    .ref("sensors/" + payload.sensores[i])
                    .update({
                      building: payload.key
                    });
                }
              }
              resolve();
            })
            .catch(error => {
              reject(error);
            });
        } else {
          firebase
            .database()
            .ref("groups" + "/" + payload.key)
            .update({
              num: payload.sensores.length
            });
          firebase
            .database()
            .ref(payload.table + "/" + payload.key)
            .update({
              key: payload.key,
              sensors: payload.sensores.sort()
            })
            .then(() => {
              resolve();
            })
            .catch(error => {
              reject(error);
            });
        }
      });
    },
    addSchedule({ commit }, payload) {
      return new Promise((resolve, reject) => {
        firebase
          .database()
          .ref("sensors/" + payload.key + "/schedules/" + payload.dias + " " + payload.timeInicio + " - " + payload.timeFin)
          .update({
            key: payload.timeInicio + " - " + payload.timeFin,
            dias: payload.dias,
            timeInicio: payload.timeInicio,
            timeFin: payload.timeFin
          })
          .then(() => {
            resolve();
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    deleteSchedule({ commit }, payload) {
      return new Promise((resolve, reject) => {
        firebase
          .database()
          .ref("sensors/" + payload.key + "/schedules/" + payload.dias + " " + payload.timeInicio + " - " + payload.timeFin + "/")
          .remove();
      });
    }
  },
  getters: {
    isAuthenticated(state) {
      return state.user !== null && state.user !== undefined;
    }
  }
});
